<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { hasPermissions } from 'dashboard/helper/permissionsHelper';

import MeusDadosIndex from './MeusDadosIndex.vue';
import ImobiliariaPerfilIndex from './ImobiliariaPerfilIndex.vue';
import CorretorVitrineIndex from './CorretorVitrineIndex.vue';
import PrivacidadeIndex from './PrivacidadeIndex.vue';
import TermosIndex from './TermosIndex.vue';
import ManualUsuarioIndex from './ManualUsuarioIndex.vue';
import DeleteAccountIndex from './DeleteAccountIndex.vue';

const SELF_SERVICE_PERMISSIONS = ['administrator', 'agent', 'custom_role'];
const ADMIN_PERMISSIONS = ['administrator'];
const AGENCY_ADMIN_PERMISSIONS = ['administrator', 'agency_manage'];

const tabs = [
  {
    label: 'Meus dados',
    section: 'meus-dados',
    componente: MeusDadosIndex,
    permissions: SELF_SERVICE_PERMISSIONS,
  },
  {
    label: 'Imobiliária',
    section: 'imobiliaria',
    componente: ImobiliariaPerfilIndex,
    permissions: AGENCY_ADMIN_PERMISSIONS,
  },
  {
    label: 'Área do corretor',
    section: 'corretor',
    componente: CorretorVitrineIndex,
    permissions: SELF_SERVICE_PERMISSIONS,
  },
  {
    label: 'Privacidade',
    section: 'privacidade',
    componente: PrivacidadeIndex,
    permissions: SELF_SERVICE_PERMISSIONS,
  },
  {
    label: 'Termos',
    section: 'termos',
    componente: TermosIndex,
    permissions: SELF_SERVICE_PERMISSIONS,
  },
  {
    label: 'Manual do usuário',
    section: 'manual',
    componente: ManualUsuarioIndex,
    permissions: SELF_SERVICE_PERMISSIONS,
  },
  {
    label: 'Deletar conta',
    section: 'deletar',
    componente: DeleteAccountIndex,
    permissions: ADMIN_PERMISSIONS,
  },
];

const route = useRoute();
const router = useRouter();
const store = useStore();

const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const currentPermissions = computed(
  () => currentAccount.value.permissions || []
);

const visibleTabs = computed(() =>
  tabs.filter(tab => hasPermissions(tab.permissions, currentPermissions.value))
);

const defaultSection = computed(
  () => visibleTabs.value[0]?.section || 'meus-dados'
);

const normalizeSection = value => {
  const requested = String(value || '');
  const match = visibleTabs.value.find(tab => tab.section === requested);
  return match ? match.section : defaultSection.value;
};

const activeSection = computed(() =>
  normalizeSection(route.params.section || 'meus-dados')
);

const activeTab = computed(
  () =>
    visibleTabs.value.find(tab => tab.section === activeSection.value) ||
    visibleTabs.value[0]
);

const currentAccountId = computed(() => route.params.accountId);

const selectSection = section => {
  const normalizedSection = normalizeSection(section);
  if (normalizedSection === route.params.section) return;

  router.push({
    name: 'conta_index',
    params: {
      accountId: currentAccountId.value,
      section: normalizedSection,
    },
  });
};

// If the requested section is missing or not permitted, redirect to the first
// visible tab so the URL always reflects an accessible section.
watch(
  () => [route.params.section, currentPermissions.value],
  () => {
    if (!visibleTabs.value.length) return;
    const normalizedSection = normalizeSection(route.params.section);
    if (normalizedSection !== route.params.section) {
      router.replace({
        name: 'conta_index',
        params: {
          accountId: currentAccountId.value,
          section: normalizedSection,
        },
      });
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="min-w-0">
    <div class="border-b border-lais-border-rule px-4 sm:px-6 lg:px-10">
      <div class="flex items-center gap-6 overflow-x-auto no-scrollbar">
        <button
          v-for="tab in visibleTabs"
          :key="tab.section"
          type="button"
          class="relative inline-flex h-12 shrink-0 items-center gap-2 text-[14px] font-semibold transition-colors"
          :class="
            activeSection === tab.section
              ? 'text-lais-primary'
              : 'text-lais-text-muted hover:text-lais-text-body'
          "
          :aria-pressed="activeSection === tab.section"
          @click="selectSection(tab.section)"
        >
          <span>{{ tab.label }}</span>
          <span
            v-if="activeSection === tab.section"
            class="absolute bottom-0 left-0 right-0 h-[3px] rounded-t bg-lais-primary"
          />
        </button>
      </div>
    </div>

    <component
      :is="activeTab.componente"
      v-if="activeTab"
      :key="activeSection"
    />
  </section>
</template>
